import { useState, useEffect, useCallback } from 'react';

export interface MobileState {
  /** 设备宽度 <= 768px（手机/小平板） */
  isMobile: boolean;
  /** 设备宽度 <= 1024px（平板及以下） */
  isTabletOrMobile: boolean;
  /** 竖屏（height > width） */
  isPortrait: boolean;
  /** 横屏（width >= height） */
  isLandscape: boolean;
  /** 手机横屏（isMobile && isLandscape） */
  isMobileLandscape: boolean;
  /** 当前视口宽度 */
  width: number;
  /** 当前视口高度 */
  height: number;
}

export function useMobileDetect(): MobileState {
  const [state, setState] = useState<MobileState>(() => computeState());

  useEffect(() => {
    let ticking = false;
    const handleChange = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setState(computeState());
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('resize', handleChange);
    window.addEventListener('orientationchange', () => setTimeout(handleChange, 150));
    return () => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
    };
  }, []);

  return state;
}

function computeState(): MobileState {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const isMobile = w <= 768;
  return {
    isMobile,
    isTabletOrMobile: w <= 1024,
    isPortrait: h > w,
    isLandscape: w >= h,
    isMobileLandscape: isMobile && w >= h,
    width: w,
    height: h,
  };
}

/**
 * 请求浏览器全屏（需在用户手势回调中调用）
 * 返回 Promise，resolve 时全屏已生效或超时/不支持
 *
 * 帮助隐藏浏览器地址栏/导航栏
 */
export function requestFullscreen(): Promise<void> {
  const el = document.documentElement as any;
  try {
    const promise: any =
      el.requestFullscreen?.() ??
      el.webkitRequestFullscreen?.() ??
      el.msRequestFullscreen?.();

    if (promise instanceof Promise) {
      return promise.catch(() => {});
    }
  } catch {
    // 不支持全屏的浏览器
  }
  return Promise.resolve();
}

/**
 * 退出全屏
 */
export function exitFullscreen() {
  try {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    }
  } catch {
    // 静默
  }
}

/**
 * 检查当前是否处于全屏状态
 */
export function isFullscreen(): boolean {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement
  );
}

/**
 * 检测是否为鸿蒙系统（非纯血，AOSP 兼容层）
 */
export function isHarmonyOS(): boolean {
  const ua = navigator.userAgent;
  return /HarmonyOS|OpenHarmony/i.test(ua);
}

/**
 * 检测是否为华为/荣耀浏览器
 * 包括鸿蒙系统自带浏览器、华为浏览器
 */
export function isHuaweiBrowser(): boolean {
  const ua = navigator.userAgent;
  return /HUAWEI|HONOR|HW|HarmonyOS|OpenHarmony/i.test(ua);
}

/**
 * 检查当前浏览器是否真正支持 document 全屏
 * iOS Safari / 鸿蒙华为浏览器：requestFullscreen 存在但仅对 video 元素有效
 */
export function isFullscreenSupported(): boolean {
  if (typeof document.documentElement.requestFullscreen === 'function') {
    const ua = navigator.userAgent;
    // iOS Safari：不支持非 video 元素的全屏
    if (/iPad|iPhone|iPod/.test(ua) && /AppleWebKit/.test(ua) && !(window as any).MSStream) {
      return false;
    }
    // 鸿蒙华为浏览器：虽然声明支持，但实际对 document 全屏支持不稳定
    if (isHuaweiBrowser()) {
      return false;
    }
    return true;
  }
  if (typeof (document.documentElement as any).webkitRequestFullscreen === 'function') {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua) && /AppleWebKit/.test(ua) && !(window as any).MSStream) {
      return false;
    }
    if (isHuaweiBrowser()) {
      return false;
    }
    return true;
  }
  return false;
}

/**
 * 监听全屏状态变化
 */
export function useFullscreenState() {
  const [fs, setFs] = useState(isFullscreen);

  useEffect(() => {
    const handler = () => setFs(isFullscreen());
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
    };
  }, []);

  return fs;
}
